/* eslint-disable @typescript-eslint/no-explicit-any */
import View from '@play-co/timestep-core/lib/ui/View';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import uiConfig from 'src/lib/ui/config';
import DropdownView from 'src/lib/ui/components/DropdownView';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { createEmitter } from 'src/lib/Emitter';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import Scroll from 'src/game/components/shared/Scroll';
import {
  Match2CheatFlags,
  match2CheatsList,
  match2CheatsUI,
} from '../match2.cheats';
import { Setting } from 'src/game/cheats/cheats.ruleset';
import { CheatData } from 'src/game/cheats/cheats.sequences';

import { AssetGroup } from '@play-co/timestep-core';

export type Item = View & { dropdown: DropdownView<CheatData> };

/**
 * Popup for match2-exclusive cheats, to be used during gameplay or in the editor.
 * This popup will filter out cheats that are not matching the editor & gameplay flags,
 * listing up only the cheats that are relevant to the current context.
 */
export default class PopupMatch2Cheats extends PopupBasic {
  private scroll: Scroll<Setting, Item>;
  private items: Item[] = [];
  private editor = false;
  private gameplay = false;
  private level = false;

  constructor(
    public creationOpts: {
      superview: View;
      close: () => void;
    },
  ) {
    super({
      superview: creationOpts.superview,
      close: creationOpts.close,

      zIndex: 19999, // on top of the world, below the cheats buttons

      width: 600,
      height: 960,
      tracking: { feature: 'cheats', $subFeature: 'popup-cheats' },
    });

    this.scroll = new Scroll({
      superview: this.box,
      createItem: this.createItem.bind(this),
      rect: {
        x: 24,
        y: 60,
        width: this.box.style.width - 48,
        height: this.box.style.height - 95,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([]);
  }

  public init(filter: Match2CheatFlags) {
    super.init({});

    this.editor = !!filter.editor;
    this.gameplay = !!filter.gameplay;
    this.level = !!filter.level;

    this.title.setText(() => 'MATCH2 CHEATS');
    this.loadScrollY();

    const cheats = match2CheatsList(filter);
    // Convert cheats object into an array.
    const result = Object.keys(cheats).map((key) => {
      return {
        key,
        setting: cheats[key],
      };
    });

    this.scroll.setItems(result as any);
  }

  public fadeOut(): void {
    this.saveScrollY();
    super.fadeOut();
  }

  private saveScrollY() {
    const scrollY = -this.scroll.getView().getOffsetY();
    localStorage.setItem(this.getLocalStorageKey(), scrollY.toString());
  }

  private loadScrollY() {
    const scrollY = Number(localStorage.getItem(this.getLocalStorageKey()));
    if (!isNaN(scrollY)) {
      this.scroll.scrollTo(0, scrollY, 1);
    }
  }

  private getLocalStorageKey() {
    return `match2-cheats-scroll-y-${this.editor}-${this.gameplay}-${this.level}`;
  }

  private createItem(
    superview: View,
    i: number,
    data: Setting,
    parent: Item | null = null,
  ) {
    const { key, setting } = data;

    const height = 73;

    const item = new View({
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      superview,
      x: 0,
      width: superview.style.width,
      height,
    }) as Item;

    const title = new LangBitmapFontTextView({
      superview: item,
      x: 20,
      y: 0,
      width: 140,
      height,
      align: 'left',
      verticalAlign: 'center',
      size: 20,
      color: 'white',
      wordWrap: false,
      font: bitmapFonts('Body'),
      localeText: () => key.toUpperCase(),
    });

    // Item is a category.
    if (!Array.isArray(setting)) {
      const content = Object.keys(setting).map((key) => ({
        key,
        setting: setting[key],
      }));

      // Use flexBox so we don't have to position items manually.
      const list = new FlexBoxView({
        superview: item,
        x: 0,
        y: height - 10,
        width: item.style.width,
        flex: {
          flexDirection: 'column',
          justifyContent: 'space-evenly',
        },
      });

      let totalHeight = 73;
      content.forEach((category, i) => {
        const subItem = this.createItem(list, i, category, parent ?? item);
        totalHeight += subItem.style.height + 10;
      });

      item.updateOpts({ height: totalHeight });
      list.updateOpts({ height: totalHeight - height });

      // Allow cattegory to be clicked to open/close.
      let collapsed = true;

      // Set collapsed.
      const setCollapsed = (collapsed: boolean) => {
        item.updateOpts({
          height: collapsed ? height : totalHeight,
          zIndex: 0,
        });

        list.updateOpts({
          visible: !collapsed,
          infinite: !collapsed,
        });

        this.scroll.reflow();
      };

      // Set initial state.
      setCollapsed(collapsed);

      // Toggle collapsed state on click.
      new ButtonScaleView({
        superview: item,
        x: 0,
        y: 0,
        width: item.style.width,
        height,
        tracking: { feature: 'cheats', $subFeature: `cheats-toggle-${key}` },
        onClick: async () => {
          setCollapsed(!collapsed);
          collapsed = !collapsed;
        },
      });

      return item;
    }

    if (key.startsWith('match2')) {
      match2CheatsUI({
        popup: this as any,
        key,
        item,
        title,
        setting,
        parent,
      });
    }

    // Make sure item is added to list.
    this.items.push(item);

    return item;
  }

  public createLabel(item: Item, data: CheatData[]) {
    const height = 73;
    const totalHeight = (data.length + 1) * height;

    item.updateOpts({ height: totalHeight });

    const list = new FlexBoxView({
      superview: item,
      x: 0,
      y: height - 10,
      width: item.style.width,
      height: item.style.height - height,
      flex: {
        flexDirection: 'column',
        justifyContent: 'space-evenly',
      },
    });

    data.forEach((data) => {
      const bg = new View({
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        superview: list,
        x: 0,
        width: list.style.width,
        height,
      }) as Item;

      const label = new LangBitmapFontTextView({
        superview: bg,
        x: 20,
        y: 0,
        width: list.style.width,
        height,
        align: 'left',
        verticalAlign: 'center',
        size: 20,
        color: 'white',
        wordWrap: false,
        font: bitmapFonts('Body'),
      });

      createEmitter(label, data!.selector!).addListener(
        (newText) =>
          (label.localeText = () => `${data.localeText!()}: ${newText}`),
      );
    });
  }

  public createMultipleButtons(item: View, count: number, data: CheatData[]) {
    const buttonData = data[0];

    const columns = Math.min(count, 10);

    const left = 14;
    const top = 34;
    const width = Math.floor(520 / columns);
    const height = 52;

    for (let i = 0; i < count; i++) {
      const x = left + width / 2 + width * (i % columns);
      const y = top + height / 2 + height * Math.floor(i / columns);

      const button = new ButtonScaleViewWithText({
        ...uiConfig.buttons.cancel,

        superview: item,
        localeText: () => (i + 1).toString(),
        fontSize: 18,
        font: bitmapFonts('Body'),
        x,
        y,
        width,
        height,
        centerOnOrigin: true,

        onClick: async () => {
          buttonData.amount = i;
          buttonData.cheat && buttonData.cheat(buttonData, button, this);
        },
        tracking: { feature: 'cheats', $subFeature: `cheat-${count}` },
      });
    }

    item.updateOpts({
      height: top + height * Math.ceil(count / columns) + 14,
    });
  }

  public createHorizontalButtons(item: View, data: CheatData[]) {
    const remain = (data.length - 1) % 2 ? 1 : 2;
    let w = (item.style.width - 5) / data.length - 5 * (data.length - remain);
    if (data.length === 1) w -= 10;

    data.map((buttonData, index) => {
      const color = buttonData.color! as keyof typeof uiConfig.buttons;
      const btnConfig = uiConfig.buttons[color] as any;
      const button = new ButtonScaleViewWithText({
        ...btnConfig,

        superview: item,
        localeText: () => buttonData.text,
        fontSize: 20,
        font: bitmapFonts('Body'),
        x: 5 + w / 2 + index * (w + 5),
        y: item.style.height * 0.5,
        width: w,
        height: 60,
        centerOnOrigin: true,

        onClick: () => {
          buttonData.cheat && buttonData.cheat(buttonData, button, this);
        },
      });
    });
  }

  public createVerticalButtons(item: View, data: CheatData[]) {
    const width = item.style.width - 15;
    const height = 65;
    const offsetY = 70;
    item.updateOpts({ height: height * data.length + offsetY - 20 });

    data.map((buttonData, index) => {
      const color = buttonData.color! as keyof typeof uiConfig.buttons;
      const btnConfig = uiConfig.buttons[color] as any;
      const button = new ButtonScaleViewWithText({
        ...btnConfig,
        superview: item,
        localeText: () => buttonData.text,
        fontSize: 20,
        font: bitmapFonts('Body'),
        x: item.style.width * 0.5,
        y: index * height + offsetY,
        width,
        height: 60,
        centerOnOrigin: true,
        onClick: () => {
          buttonData.cheat && buttonData.cheat(buttonData, button, this);
        },
      });
    });
  }
}
